import { Component, EventEmitter, Input, Optional, Output, OnDestroy } from '@angular/core';
import { CasinoGameListService } from '@services/casino-game-list.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '@services/account.service';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LoginModalComponent } from '@app/authentication/login-modal/login-modal.component';
import { CasinoFilterComponent } from '../casino-filter/casino-filter.component';
import { Overlay } from '@angular/cdk/overlay';
import { CasinoProvider } from '@app/shared/models/interfaces/casinoProvider';
import { PragmaticLiveStatusService } from '@app/services/pragmatic-live-status.service';
import {
    Observable,
    Subject,
    Subscription,
    debounceTime,
    distinctUntilChanged,
    forkJoin,
    map,
    of,
    switchMap,
    takeUntil,
} from 'rxjs';

@Component({
    selector: 'app-casino-search',
    imports: [CommonModule, FormsModule, TranslocoModule],
    templateUrl: './casino-search.component.html',
    styleUrl: './casino-search.component.scss',
})
export class CasinoSearchComponent implements OnDestroy {
    casinoGameList: any[] = [];
    pageNumber: number = 1;
    noSearchResults: boolean = false;
    loadingGames = true;
    showPagination = false;
    totalElements: number = 0;
    size: number = 88;
    showLoadMore: boolean = false;
    modalVisible: boolean = false;
    totalPages: any;

    //Filter
    showFilter: boolean = false;
    selectedProviders: string[] = [];
    providers: CasinoProvider[] = [];
    allProviders: CasinoProvider[] = [];
    firstCasinoGameList: any[] = [];
    allCasinoGames: any[] = [];

    searchText = '';
    searchMode: 'name' | 'provider' = 'name';
    matchedProviderNames: string[] = [];
    placeholderText: string = this.translateService.translate(
        'casino_page.search_placeholder_text',
    );
    showClearButton: boolean = false;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();
    private gamesDecorationSubscription?: Subscription;

    @Input() favouriteGames: any = [];
    @Input() gameSelected = false;
    @Input() showCloseButton: boolean = true;
    @Input() showSearch: boolean = false;
    @Output() closeSearch = new EventEmitter<void>();
    @Input() iframes: any[] = [];
    @Input() iframeIndex?: number;
    @Input() onGameSelected?: (gameId: number, iframeNumber?: number, provider?: string) => void;

    constructor(
        private casinoGamesService: CasinoGameListService,
        private router: Router,
        private accountService: AccountService,
        public dialog: MatDialog,
        public language: TranslocoService,
        private translateService: TranslocoService,
        @Optional() private dialogRef: MatDialogRef<CasinoSearchComponent>,
        private activatedRoute: ActivatedRoute,
        private overlay: Overlay,
        private pragmaticLiveStatusService: PragmaticLiveStatusService,
    ) {
        language.setDefaultLang('es');
        this.casinoGamesService.excludedChange.subscribe({
            next: () => {
                if (this.iframeIndex != undefined && this.iframes[this.iframeIndex]?.showGameList) {
                    this.searchGames();
                } else {
                    this.searchGames();
                }
            },
        });

        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((searchTerm: string) => {
                if (searchTerm.length >= 1) {
                    this.searchGames();
                } else if (searchTerm.length === 0) {
                    this.handleEmptySearch();
                }
            });
    }

    searchGames(providers?: string[]): void {
        if (!this.searchText.trim()) {
            this.pageNumber = 0;
            this.searchMode = 'name';
            this.matchedProviderNames = [];
            this.handleEmptySearch();
        } else {
            this.pageNumber = 0;
            this.resolveSearchRequest(providers).subscribe({
                    next: (response) => {
                        this.updateSearchResults(
                            response.content,
                            response.totalPages,
                            response.totalElements,
                            true,
                        );
                        this.showFilter = response.content.length > 1;

                        if (this.searchMode === 'provider') {
                            this.providers = this.getMatchedProvidersFromCache();
                        } else {
                            this.getProviders(this.searchText);
                        }
                    },
                    error: (error) => {
                        console.error('Error fetching games based on search:', error);
                    },
                });
        }
    }

    getProviders(name: string): void {
        this.casinoGamesService.getGameProviders({ type: '', name: name }).subscribe({
            next: (response) => {
                this.providers = response;
            },
        });
    }

    openCasinoFilter(): void {
        const dialogOpen = this.dialog.open(CasinoFilterComponent, {
            panelClass: 'full-screen-dialog',
            disableClose: false,
            autoFocus: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            data: { providers: this.providers, selectedProviders: this.selectedProviders },
        });

        dialogOpen.afterClosed().subscribe((selectedProviders: string[]) => {
            if (selectedProviders) {
                this.selectedProviders = selectedProviders;
                this.searchGames(selectedProviders);
            }
        });
    }

    removeProvider(provider: string): void {
        this.selectedProviders = this.selectedProviders.filter((p) => p !== provider);
        this.searchGames(this.selectedProviders);
    }

    handleKeydown(event) {
        if (event.key === 'Enter' || event.keyCode === 13) {
            this.searchGames();
        }
    }

    isFavourite(game: any): boolean {
        return this.favouriteGames.some((favGame) => favGame.id === game.id);
    }

    removeFromFavourites(game: any): void {
        this.favouriteGames = this.favouriteGames.filter((favGame) => favGame.id !== game.id);
        this.casinoGamesService.deleteFavourite(game.id);
    }

    addToFavourites(game: any): void {
        const isLoggedIn = this.accountService.isAuthenticated().value;
        if (isLoggedIn) {
            if (!this.isFavourite(game)) {
                this.favouriteGames.push(game);
                this.casinoGamesService.updateFavourite(game.id);
            }
        } else {
            this.dialog.open(LoginModalComponent, {
                panelClass: 'full-screen-dialog',
                scrollStrategy: this.overlay.scrollStrategies.noop(),
            });
        }
    }

    loadFavouriteGames(): void {
        this.casinoGamesService.getFavouriteGames().subscribe({
            next: (favouriteGames) => {
                this.favouriteGames = favouriteGames;
            },
            error: (error) => {
                console.error('Error loading favourite games', error);
            },
        });
    }

    clearPlaceholder() {
        this.placeholderText = '';
    }

    toggleClearButton() {
        this.showClearButton = this.searchText.length > 0;
        this.searchSubject.next(this.searchText);
    }

    restorePlaceholder() {
        if (!this.searchText) {
            this.placeholderText = this.translateService.translate(
                'casino_page.search_placeholder_text',
            );
        }
    }

    clearSearch(): void {
        this.searchText = '';
        this.searchMode = 'name';
        this.matchedProviderNames = [];
        this.toggleClearButton();
    }

    loadGames(): void {
        this.loadingGames = true;
        this.noSearchResults = false;

        if (this.isLiveCasinoContext()) {
            this.loadLiveCasinoGames();
            return;
        }

        this.casinoGamesService
            .getGames({ name: this.searchText, page: this.pageNumber, size: this.size })
            .subscribe({
                next: (response) => {
                    this.updateSearchResults(response.content, response.totalPages, response.totalElements);
                    this.gameSelected = false;
                },
                error: (error) => {
                    console.error('Failed to fetch games', error);
                    this.loadingGames = false;
                },
            });
    }

    loadMoreGames(): void {
        this.loadingGames = true;
        this.noSearchResults = false;
        this.pageNumber++;

        if (this.isLiveCasinoContext() && !this.searchText.trim()) {
            this.loadMoreLiveCasinoGames();
            return;
        }

        this.fetchGamesByProviders(
            this.selectedProviders.length > 0 ? this.selectedProviders : undefined,
            this.pageNumber,
        )
            .subscribe({
                next: (response) => {
                    this.updateSearchResults(
                        [...this.casinoGameList, ...response.content],
                        response.totalPages ?? this.totalPages,
                        response.totalElements ?? this.totalElements,
                    );
                    this.showLoadMore = this.totalElements > this.casinoGameList.length;
                },
                error: (error) => {
                    console.error('Failed to fetch more games', error);
                    this.loadingGames = false;
                },
            });
    }

    private resolveSearchRequest(providers?: string[]): Observable<any> {
        if (providers && providers.length > 0) {
            return this.fetchGamesByProviders(providers, this.pageNumber);
        }

        return this.getAllProviders().pipe(
            switchMap((allProviders) => {
                const normalizedSearch = this.searchText.trim().toLowerCase();
                const matchedProviders = allProviders.filter((provider) =>
                    provider.provider.toLowerCase().includes(normalizedSearch),
                );

                this.matchedProviderNames = matchedProviders.map((provider) => provider.provider);
                this.searchMode = this.matchedProviderNames.length > 0 ? 'provider' : 'name';

                return this.fetchGamesByProviders(
                    this.searchMode === 'provider' ? this.matchedProviderNames : undefined,
                    this.pageNumber,
                );
            }),
        );
    }

    private buildSearchParams(
        providers: string[] | undefined,
        page: number,
    ): { name?: string; page: number; size: number; categories?: string[] } {
        const params: { name?: string; page: number; size: number; categories?: string[] } = {
            page,
            size: this.size,
        };

        if (this.searchMode === 'name') {
            params.name = this.searchText;
        }

        if (providers && providers.length > 0) {
            params.categories = providers;
        }

        return params;
    }

    private getAllProviders(): Observable<CasinoProvider[]> {
        if (this.allProviders.length > 0) {
            return of(this.allProviders);
        }

        return this.casinoGamesService.getGameProviders().pipe(
            map((providers) => {
                this.allProviders = providers;
                return providers;
            }),
        );
    }

    private getMatchedProvidersFromCache(): CasinoProvider[] {
        return this.allProviders.filter((provider) =>
            this.matchedProviderNames.includes(provider.provider),
        );
    }

    private handleEmptySearch(): void {
        this.pageNumber = 0;
        this.loadingGames = true;
        this.noSearchResults = false;

        if (this.selectedProviders.length > 0) {
            this.fetchGamesByProviders(this.selectedProviders, this.pageNumber).subscribe({
                next: (response) => {
                    this.casinoGameList = response.content;
                    this.firstCasinoGameList = this.casinoGameList;
                    this.totalPages = response.totalPages;
                    this.totalElements = response.totalElements;
                    this.loadingGames = false;
                    this.gameSelected = false;
                    this.showPagination = this.totalPages > 1;
                    this.noSearchResults = this.casinoGameList.length === 0;
                    this.showFilter = true;
                    this.providers = this.getSelectedProvidersFromCache();
                },
                error: (error) => {
                    console.error('Failed to fetch filtered games', error);
                    this.loadingGames = false;
                },
            });
            return;
        }

        this.loadGames();
        this.showFilter = false;
    }

    private getSelectedProvidersFromCache(): CasinoProvider[] {
        const providerSource = this.allProviders.length > 0 ? this.allProviders : this.providers;
        return providerSource.filter((provider) => this.selectedProviders.includes(provider.provider));
    }

    private fetchGamesByProviders(providers: string[] | undefined, page: number): Observable<any> {
        if (!providers || providers.length <= 1) {
            return this.casinoGamesService.getGames(this.buildSearchParams(providers, page));
        }

        return forkJoin(
            providers.map((provider) =>
                this.casinoGamesService.getGames(this.buildSearchParams([provider], page)),
            ),
        ).pipe(
            map((responses) => {
                const uniqueGames = new Map<number, any>();

                responses.forEach((response) => {
                    response.content.forEach((game: any) => {
                        uniqueGames.set(game.id, game);
                    });
                });

                return {
                    content: Array.from(uniqueGames.values()),
                    totalElements: Array.from(uniqueGames.values()).length,
                    totalPages: Math.max(...responses.map((response) => response.totalPages ?? 0)),
                };
            }),
        );
    }

    loadLiveCasinoGames(): void {
        this.loadingGames = true;
        this.noSearchResults = false;
        this.casinoGamesService.getLiveCasinoGames(this.pageNumber, 150).subscribe({
            next: (response) => {
                this.updateSearchResults(response.content, response.totalPages, response.totalElements);
                this.gameSelected = false;
            },
            error: (error) => {
                console.error('Failed to fetch games', error);
                this.loadingGames = false;
            },
        });
    }

    loadMoreLiveCasinoGames(): void {
        this.casinoGamesService.getLiveCasinoGames(this.pageNumber, 150).subscribe({
            next: (response) => {
                const nextGames = Array.isArray(response?.content) ? response.content : [];
                const existingIds = new Set(this.casinoGameList.map((game) => game.id));
                const mergedGames = [
                    ...this.casinoGameList,
                    ...nextGames.filter((game) => !existingIds.has(game.id)),
                ];

                this.updateSearchResults(
                    mergedGames,
                    response?.totalPages ?? this.totalPages,
                    response?.totalElements ?? this.totalElements,
                );
                this.showLoadMore = (response?.totalElements ?? 0) > mergedGames.length;
            },
            error: (error) => {
                console.error('Failed to fetch more live casino games', error);
                this.loadingGames = false;
            },
        });
    }

    playRandomGame(): void {
        if (this.checkLogin()) {
            this.casinoGamesService.getRandomGameId().subscribe({
                next: (id) => {
                    this.onGameSelectedHandler(id);
                },
            });
        }
    }

    checkLogin(): boolean {
        const isLoggedIn = this.accountService.isAuthenticated().value;
        if (!isLoggedIn) {
            this.openLoginModal();
            return false;
        }
        this.modalVisible = true;
        return true;
    }

    openLoginModal(): void {
        const element = document.querySelector('[data-bs-target="#signin"]') as HTMLElement;
        if (element) {
            element.click();
        } else {
            console.error('Element not found');
        }
    }

    onGameSelectedHandler(gameId: number, iframeNumber?: number, provider?: string) {
        if (this.onGameSelected) {
            this.onGameSelected(gameId, iframeNumber, provider);
        } else {
            this.defaultOnGameSelected(gameId);
        }
    }

    onGameClick(gameId: number, index: number): void {
        if (this.onGameSelected) {
            this.onGameSelected(gameId, index + 1);
        }
    }

    defaultOnGameSelected(gameId: number): void {
        const isLoggedIn = this.accountService.isAuthenticated().value;
        if (!isLoggedIn) {
            const queryParams = { ...this.activatedRoute.snapshot.queryParams };
            queryParams['returnUrl'] = `casino/play/${gameId}`;

            // Add param to url before opening Login Modal
            this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: queryParams,
                queryParamsHandling: 'merge',
            });
            const dialogRef = this.dialog.open(LoginModalComponent, {
                panelClass: 'full-screen-dialog',
                scrollStrategy: this.overlay.scrollStrategies.noop(),
            });
        } else {
            this.router.navigate(['casino/play', gameId]);
        }
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    closeModal() {
        if (this.dialogRef) {
            this.dialogRef.close();
        } else {
            this.showSearch = false;
            this.closeSearch.emit();
        }
    }

    ngOnDestroy(): void {
        this.gamesDecorationSubscription?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private isLiveCasinoContext(): boolean {
        return this.router.url.includes('/live-casino');
    }

    private updateSearchResults(
        games: any[],
        totalPages: number,
        totalElements: number,
        updateFirstList: boolean = false,
    ): void {
        this.gamesDecorationSubscription?.unsubscribe();
        this.gamesDecorationSubscription = this.pragmaticLiveStatusService
            .decorateGames$(games, { enableLiveStatus: this.router.url.includes('/live-casino') })
            .subscribe({
            next: (decoratedGames) => {
                this.casinoGameList = decoratedGames;
                if (updateFirstList) {
                    this.firstCasinoGameList = decoratedGames;
                }
                this.totalElements = totalElements;
                this.totalPages = totalPages;
                this.loadingGames = false;
                this.showPagination = this.totalPages > 1;
                this.noSearchResults = this.casinoGameList.length === 0;
            },
            error: () => {
                this.casinoGameList = games;
                if (updateFirstList) {
                    this.firstCasinoGameList = games;
                }
                this.totalElements = totalElements;
                this.totalPages = totalPages;
                this.loadingGames = false;
                this.showPagination = this.totalPages > 1;
                this.noSearchResults = this.casinoGameList.length === 0;
            },
            });
    }
}
